import {
  HeaderGroup,
  Row,
  Table,
  getCoreRowModel,
  useReactTable,
  flexRender,
  RowData,
  TableOptions,
  HeaderContext,
  CellContext,
  getFilteredRowModel,
  VisibilityState,
  getFacetedUniqueValues,
  getSortedRowModel,
  TableState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { noop, omit, times } from "lodash";
import React, { useRef, useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";
import { useLocalStorage } from "usehooks-ts";

import { ColumnVisibilityDropdown as ColumnVisibilityDropdownBase } from "src/base-components/ColumnVisibilityDropdown";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";

// We actually expect any functions here and can not be explicitly typed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TableActions = Record<string, (...args: any[]) => void>;
type TableVariant = "default" | "compact";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface TableMeta<TData extends RowData> {
    actions?: TableActions;
    variant?: TableVariant;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    displayName?: string | null;
    sticky?: "left" | "right";
  }
}

type RowProps = {
  classNameOverrides?: string;
  [key: string]: unknown;
};
type HeaderGroupProps = {
  classNameOverrides?: string;
  [key: string]: unknown;
};

type Props<T extends object, A extends TableActions> = {
  data: T[];
  columns: TableOptions<T>["columns"];
  frameClassName?: string;
  rowClassName?: string;
  rowPropsGetter?: (rowData: Row<T>) => RowProps;
  headerPropsGetter?: (headerProps: HeaderGroup<T>) => HeaderGroupProps;
  renderTableHeader?: (tableInstance: Table<T>) => React.ReactNode;
  noScroll?: boolean;
  dataLoc?: string;
  isLoading?: boolean;
  actions?: A;
  getRowId?: TableOptions<T>["getRowId"];
  loadingRowsCount?: number;
  onScrolledToTheBottom?: () => void;
  isFetchingNextPage?: boolean;
  variant?: TableVariant;
  globalFilter?: string;
  // Used to store the visual state of the table
  uiStoreKey?: string;
  clientSideFiltering?: boolean;
  initialState?: TableOptions<T>["initialState"];
  rowSelection?: TableState["rowSelection"];
  columnVisibility?: TableState["columnVisibility"];
  onRowSelectionChange?: TableOptions<T>["onRowSelectionChange"];
  disableVirtualization?: boolean;
};

const ROW_CLASS_NAMES =
  "[&:not(:last-child)]:border-b group group/row border-gray-100";
const HEADER_CLASS_NAMES =
  "sticky top-0 bg-opacity-100 bg-white border-b border-gray-100 font-inter-medium-12px text-left z-20";

export const TableComp = <T extends object, A extends TableActions>({
  data,
  columns,
  frameClassName,
  rowClassName,
  rowPropsGetter,
  headerPropsGetter,
  noScroll = false,
  isLoading = false,
  loadingRowsCount = 10,
  dataLoc,
  actions,
  getRowId,
  onScrolledToTheBottom,
  isFetchingNextPage,
  variant = "default",
  globalFilter,
  clientSideFiltering = false,
  initialState,
  uiStoreKey,
  rowSelection,
  onRowSelectionChange,
  columnVisibility: defaultColumnVisibility,
  disableVirtualization = false,
}: Props<T, A>) => {
  const [columnVisibility, setColumnVisibility] = useVisibilityStore(
    uiStoreKey,
    defaultColumnVisibility,
  );
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: clientSideFiltering
      ? getFilteredRowModel()
      : undefined,
    getFacetedUniqueValues: clientSideFiltering
      ? getFacetedUniqueValues()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: { size: undefined },
    meta: { actions, variant },
    getRowId,
    state: {
      globalFilter,
      columnVisibility,
      rowSelection,
    },
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: !!rowSelection,
    initialState,
    onRowSelectionChange,
  });
  const { getHeaderGroups } = tableInstance;

  const headerGroups = getHeaderGroups();
  const rows = tableInstance.getRowModel().rows;

  const renderHeaderGroup = (headerGroup: HeaderGroup<T>) => {
    const headerProps = headerPropsGetter?.(headerGroup);

    return (
      <tr
        key={headerGroup.id}
        className={twMerge(HEADER_CLASS_NAMES, headerProps?.classNameOverrides)}
        {...omit(headerProps, "classNameOverrides")}
      >
        {headerGroup.headers.map((column) => {
          const ctx = column.getContext();
          const sticky = column.column.columnDef.meta?.sticky;

          return (
            <th
              key={column.id}
              className={twJoin(
                "p-0",
                sticky === "left" && "sticky left-0",
                sticky === "right" && "sticky right-0",
              )}
              colSpan={column.colSpan}
              style={{
                minWidth: ctx.column.columnDef.minSize,
                maxWidth: ctx.column.columnDef.maxSize,
                width: ctx.column.columnDef.size ? column.getSize() : undefined,
              }}
            >
              {flexRender(column.column.columnDef.header, column.getContext())}
            </th>
          );
        })}
      </tr>
    );
  };

  const renderRow = (row: Row<T>) => {
    const rowProps = rowPropsGetter?.(row);
    return (
      <tr
        key={row.id}
        className={twMerge(
          ROW_CLASS_NAMES,
          rowClassName,
          rowProps?.classNameOverrides,
        )}
        {...omit(rowProps, "classNameOverrides")}
      >
        {row.getVisibleCells().map((cell) => {
          const sticky = cell.column.columnDef.meta?.sticky;
          return (
            // h-[inherit] & p-0 allow the content inside td to take full width/height
            <td
              key={cell.id}
              className={twJoin(
                "h-[inherit] p-0",
                sticky === "left" && "sticky left-0",
                sticky === "right" && "sticky right-0",
              )}
              style={{
                minWidth: cell.column.columnDef.minSize,
                maxWidth: cell.column.columnDef.maxSize,
                width: cell.column.columnDef.size
                  ? cell.column.getSize()
                  : undefined,
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderLoadingRow = (index: number) => {
    return (
      <tr
        key={index}
        className={(twMerge(ROW_CLASS_NAMES, rowClassName), "cursor-default")}
      >
        {columns.map((column, i) => {
          return (
            <td key={`${column.id}_${i}`} className="relative h-[inherit] p-0">
              <div
                className={twJoin(
                  "flex items-center p-1.5",
                  variant === "compact" ? "min-h-[32px]" : "min-h-[48px]",
                )}
              >
                <SkeletonPlaceholder height="h-5" width="w-4/5" />
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: rows.length,
    estimateSize: () => 36,
    overscan: 10,
    enabled: !disableVirtualization,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowSize = rowVirtualizer.getTotalSize();
  // Dynamic top and botton padding is added to the table content to maintain the correct scroll bar size and position
  const paddingTop = virtualRows?.[0]?.start || 0;
  const paddingBottom =
    totalRowSize - (virtualRows?.[virtualRows.length - 1]?.end || 0);

  const debouncedOnScrolledToTheBottom = useDebouncedCallback(
    onScrolledToTheBottom ?? noop,
    1000,
    {
      leading: true,
      trailing: false,
    },
  );

  const onScroll = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < 1000) {
        debouncedOnScrolledToTheBottom();
      }
    }
  };

  const renderRows = () => {
    if (isLoading) {
      return times(loadingRowsCount, (i) => renderLoadingRow(i));
    }
    if (disableVirtualization) {
      return rows.map((row) => renderRow(row));
    }
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];
      return renderRow(row);
    });
  };

  return (
    <div
      ref={tableContainerRef}
      className={twMerge(
        "relative isolate h-full",
        !noScroll && "decideScrollbar overflow-auto",
        frameClassName,
      )}
      data-loc={dataLoc}
      onScroll={(e) =>
        onScrolledToTheBottom ? onScroll(e.target as HTMLDivElement) : undefined
      }
    >
      <table
        className={twJoin(
          "w-full table-auto border-collapse border-spacing-0 text-gray-800",
          variant === "compact" && "border-b border-gray-100",
        )}
      >
        <thead>{headerGroups.map(renderHeaderGroup)}</thead>
        <tbody>
          {!disableVirtualization && paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {renderRows()}
          {isFetchingNextPage &&
            times(loadingRowsCount, (i) => renderLoadingRow(i))}
          {!isLoading && !disableVirtualization && paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const Header = <D extends object, V>({
  children,
  info,
  dataLoc,
}: {
  children?: React.ReactNode;
  info: HeaderContext<D, V>;
  dataLoc?: string;
}) => {
  if (!children) {
    // If no children we consider it as a placeholder and return null
    return null;
  }

  const variant = info.table.options.meta?.variant ?? "default";
  const sticky = info.column.columnDef.meta?.sticky;

  return (
    <div
      className={twJoin(
        "flex items-center",
        "pl-1.5 pr-2",

        variant === "default" && "h-12",
        variant === "compact" && "h-8",

        sticky && "sticky z-20 bg-white",
        sticky === "left" && "left-0",
        sticky === "right" && "right-0",
      )}
      data-loc={dataLoc}
    >
      <div className="text-gray-800 font-inter-medium-12px">{children}</div>
    </div>
  );
};

export const Cell = <D extends object, V>({
  children,
  className,
  dataLoc,
  info,
}: {
  children?: React.ReactNode;
  className?: string;
  dataLoc?: string;
  info: CellContext<D, V>;
}) => {
  const variant = info.table.options.meta?.variant ?? "default";
  const sticky = info.column.columnDef.meta?.sticky;

  return (
    <div
      className={twMerge(
        "flex items-center truncate text-gray-800 font-inter-normal-12px",
        "pl-1.5 pr-2",

        variant === "default" && "h-12",
        variant === "compact" && "h-8 py-1.5",

        sticky && "sticky z-10 bg-white",
        sticky === "left" && "left-0",
        sticky === "right" && "right-0",
        className,
      )}
      data-loc={dataLoc}
    >
      {children}
    </div>
  );
};

/**
 * By default, this component allows to hide/show columns for given table.
 * But it is not stored anywhere.
 * If uiStoreKey is provided to the table component,
 * it will use the local storage to store the visibility state.
 */
export const ColumnVisibilityDropdown = <TData extends RowData>({
  dataLoc,
  table,
  min,
  max,
}: {
  dataLoc?: string;
  table: Table<TData>;
  min?: number;
  max?: number;
}) => {
  const columns = table
    .getAllLeafColumns()
    // Skip display columns, use only columns with actual data
    .filter((column) => column.accessorFn);
  const visibleColumns = table.getVisibleLeafColumns();
  const onChange = (value: string[]) => {
    table.setColumnVisibility(
      columns.reduce((acc, col) => {
        acc[col.id] = value.includes(col.id);
        return acc;
      }, {} as VisibilityState),
    );
  };

  return (
    <ColumnVisibilityDropdownBase
      dataLoc={dataLoc}
      items={columns.map((column) => ({
        label: column.columnDef.meta?.displayName ?? column.id,
        value: column.id,
        disabled: !column.getCanHide()
          ? "This column is required and cannot be hidden"
          : undefined,
      }))}
      max={max}
      min={min}
      value={visibleColumns.map((column) => column.id)}
      onChange={onChange}
      onReset={table.resetColumnVisibility}
    />
  );
};

const useVisibilityStore = (key: string | undefined, defaultValue = {}) => {
  const NOOP_STORE_KEY = "NOOP_STORE_KEY";
  const visibilityLocalStorage = useLocalStorage(
    key ?? NOOP_STORE_KEY,
    defaultValue,
  );
  const visibilityLocalState = useState(defaultValue);

  // If no key is provided, just use internal table state
  return key ? visibilityLocalStorage : visibilityLocalState;
};
